// src/sim/lighting/bitset.ts — Uint32Array bitset helpers (Pitfall 1: lock the type).
//
// Invariants:
//   - Zero allocations: callers preallocate once; set/get/clear mutate in place.
//   - Out-of-bounds: setBit/getBit return false (not throw). Clear sets all words to 0.
//   - No side effects on other modules.
//
// Source: .planning/phases/02-fov-lighting/02-RESEARCH.md §Pitfall 1 + §Don't Hand-Roll (Bitset row).

export function allocBitset(widthInTiles: number, heightInTiles: number): Uint32Array {
  const words = Math.ceil((widthInTiles * heightInTiles) / 32);
  // P2 D-16 / Pitfall 4 (updated 02-06 Gap 1 closure): the bitset budget is
  // 40,960 tiles = 1,280 Uint32 words, which matches docs/map-authoring.md §4
  // "Chosen maximum: 256 × 160 tiles". The previous 512-word (16k-tile) cap
  // contradicted that ceiling and threw at runtime on the 160×144 Mouchao map
  // (720 words needed) during the first torch-on transition — see
  // .planning/debug/torch-button-noop-and-input-freeze.md for the forensic
  // trace. Larger maps than the documented ceiling would require a different
  // allocation strategy (chunked bitsets or sparse set); fail loud at boot
  // instead of silently truncating.
  if (words > 1280) {
    throw new Error(
      'FOV bitset exceeds 40k-tile budget (1,280 Uint32 words) — map too large for current allocation strategy; see docs/map-authoring.md §4',
    );
  }
  return new Uint32Array(words);
}

export function setBit(arr: Uint32Array, idx: number): void {
  if (idx < 0) return;
  const wordIdx = idx >>> 5;
  if (wordIdx >= arr.length) return;
  arr[wordIdx] = (arr[wordIdx] ?? 0) | (1 << (idx & 31));
}

export function getBit(arr: Uint32Array, idx: number): boolean {
  if (idx < 0) return false;
  const wordIdx = idx >>> 5;
  if (wordIdx >= arr.length) return false;
  return ((arr[wordIdx] ?? 0) & (1 << (idx & 31))) !== 0;
}

export function clearBitset(arr: Uint32Array): void {
  arr.fill(0);
}
